/**
 * IndexedDB database manager.
 * Wraps indexedDB.open in a Promise with typed upgrade callback.
 */

export type UpgradeCallback = (
  db: IDBDatabase,
  oldVersion: number,
  transaction: IDBTransaction,
) => void;

export function openDatabase(
  name: string,
  version: number,
  onUpgrade: UpgradeCallback,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, version);

    request.onupgradeneeded = (event) => {
      // request.transaction is the versionchange transaction — required to alter
      // an existing object store (e.g. add an index) during an upgrade.
      onUpgrade(request.result, event.oldVersion, request.transaction as IDBTransaction);
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    // Another tab/window holds an older version open, blocking this upgrade.
    // Reject with a clear error instead of hanging the promise forever.
    request.onblocked = () =>
      reject(
        new Error(
          `IndexedDB "${name}" upgrade to v${version} is blocked by another open connection ` +
            `(likely another tab). Close other tabs/windows of this app and retry.`,
        ),
      );
  });
}

/** Helper to get an object store from a database. */
export function getStore(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
): IDBObjectStore {
  return db.transaction(storeName, mode).objectStore(storeName);
}

/** Wrap an IDBRequest in a Promise. */
export function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
